// apps/web/src/features/admin/form-config/state/store.ts
import { create } from 'zustand';
import type {
  JobTypeVersionContent,
  JobTypeVersionContentV3,
  FormContent,
  FieldContent,
  Behavior,
  Stage,
  FormIdentity,
  Profile,
  RuleAttachment,
  CreateRuleAttachmentInput,
  ActionConfig,
  ExportDestinationContent,
  ExportStructureNode,
  ConditionGroup,
  FormRecognitionConfig,
  FormDestinationMapping,
  RemittanceJobConfig,
  RemittanceRole,
  ConstantDecl,
} from '@capture/shared';
import {
  createDefaultProfile,
  createFallbackProfile,
  createRuleAttachment,
  migrateContentToV3,
  flattenToLegacyContent,
  getDefaultForm,
  getFormContent,
  upsertFormContent,
} from '@capture/shared';
import type { JobTypeVersion } from '../api/client';

// ---------------------------------------------------------------------------
// V3 expand-contract helpers
//
// `contentV3` is the source of truth; `content` is a DERIVED legacy (V2) view
// recomputed via `flattenToLegacyContent` after every mutation so the ~28
// un-migrated readers (FieldListPane, sections/*, RulesTab, Export*, Header*,
// Pipeline*, validators, useDebouncedSave) keep working unchanged.
//
// Invariant: after any write touching content, `content === flatten(contentV3)`
// (or both null). `withV3` is the single place that produces both fields, so no
// action can forget to re-derive.
// ---------------------------------------------------------------------------

/** Produces the dual content fields from a V3 blob. Use everywhere content changes. */
function withV3(next: JobTypeVersionContentV3): {
  contentV3: JobTypeVersionContentV3;
  content: JobTypeVersionContent;
} {
  return { contentV3: next, content: flattenToLegacyContent(next) };
}

/**
 * Resolves the working V3 blob from state. In production `loadVersion` always
 * sets `contentV3`, so this returns it directly. For tests (and any legacy code
 * path) that seed only the derived `content` via `setState`, this migrates it
 * on the fly so every action stays V3-correct regardless of seeding path.
 */
export function resolveV3(s: {
  contentV3: JobTypeVersionContentV3 | null;
  content: JobTypeVersionContent | null;
}): JobTypeVersionContentV3 | null {
  if (s.contentV3) return s.contentV3;
  if (s.content) return migrateContentToV3(s.content);
  return null;
}

/** Active form, falling back to the default form when activeFormId is null/stale. */
export function activeForm(v3: JobTypeVersionContentV3, activeFormId: string | null): FormContent {
  return getFormContent(v3, activeFormId ?? '') ?? getDefaultForm(v3);
}

/** Re-maps one field's stage behaviors on a form, leaving every other field untouched. */
function mapFieldStage(
  form: FormContent,
  fieldName: string,
  stage: Stage,
  fn: (behaviors: Behavior[]) => Behavior[],
): FormContent {
  return {
    ...form,
    fields: form.fields.map((f) =>
      f.name === fieldName
        ? { ...f, stageMap: { ...f.stageMap, [stage]: fn(f.stageMap[stage] ?? []) } }
        : f,
    ),
  };
}

/**
 * Reducer for an active-form mutation: resolve the V3 blob, transform the
 * active form, upsert it back, re-derive the legacy view, and mark dirty.
 * Returns `null` when no content is loaded so callers can no-op. Centralizing
 * this is what makes the "every write re-derives via withV3" invariant
 * structural rather than convention-enforced.
 */
function patchActiveForm(
  s: { contentV3: JobTypeVersionContentV3 | null; content: JobTypeVersionContent | null; activeFormId: string | null },
  fn: (form: FormContent) => FormContent,
): { contentV3: JobTypeVersionContentV3; content: JobTypeVersionContent; dirty: true } | null {
  const v3 = resolveV3(s);
  if (!v3) return null;
  return { ...withV3(upsertFormContent(v3, fn(activeForm(v3, s.activeFormId)))), dirty: true };
}

/** Reducer for a job-level (top-of-blob) V3 mutation. Returns `null` when no content is loaded. */
function patchV3(
  s: { contentV3: JobTypeVersionContentV3 | null; content: JobTypeVersionContent | null },
  fn: (v3: JobTypeVersionContentV3) => JobTypeVersionContentV3,
): { contentV3: JobTypeVersionContentV3; content: JobTypeVersionContent; dirty: true } | null {
  const v3 = resolveV3(s);
  if (!v3) return null;
  return { ...withV3(fn(v3)), dirty: true };
}

interface FormConfigState {
  version: JobTypeVersion | null;
  /** Source of truth — per-form V3 content. */
  contentV3: JobTypeVersionContentV3 | null;
  /** Derived legacy (V2) view of `contentV3`, kept in sync via `withV3`. */
  content: JobTypeVersionContent | null;
  /** Id of the form currently being edited (drives field/rule/recognition writes). */
  activeFormId: string | null;
  selectedFieldName: string | null;
  /**
   * R-117: controls whether the field editor drawer is open.
   * `setSelectedField` selects/highlights a row but does NOT open the drawer.
   * `openFieldEditor(name)` selects the field AND opens the drawer (called by
   * the Edit menu item). Closing the drawer calls `setSelectedField(null)` which
   * also resets this flag.
   */
  fieldEditorOpen: boolean;
  dirty: boolean;
  saving: boolean;
  lastSavedAt: Date | null;
  /**
   * F-032 — true after a draft write was rejected as stale (another surface
   * wrote the draft since this one loaded it). While set, the autosave loop is
   * suspended and the workspace shows a blocking reload banner — the two safe
   * outcomes are "reload and re-do this screen's edits" or nothing; silently
   * retrying would clobber the other surface's slices.
   */
  conflicted: boolean;
  advancedMode: boolean;

  loadVersion: (v: JobTypeVersion) => void;
  adoptDraft: (v: JobTypeVersion) => void;
  /**
   * F-009 — after Add Form, absorb the server draft's freshly-appended form
   * slice into local contentV3 WITHOUT clobbering local edits to other forms
   * (the server draft may be staler than the store), and adopt the draft's
   * identity so the autosave targets the DRAFT (Add Form may have created it
   * server-side while the store still held the published version). Without
   * this, activeForm() misses the new form, falls back to Default — routing
   * every field write there — and the wholesale updateDraft autosave wipes
   * the server-appended slice.
   */
  absorbServerForm: (v: JobTypeVersion, formId: string) => void;
  markSaving: () => void;
  /** F-032 — adopts the server's incremented contentRevision from the save response. */
  markSaved: (newRevision: number) => void;
  markSaveFailed: () => void;
  markConflicted: () => void;
  setSelectedField: (name: string | null) => void;
  openFieldEditor: (name: string) => void;
  setAdvancedMode: (on: boolean) => void;

  // Form-scoped selectors/actions (Plan 2 Task 1 deliverable)
  selectForm: (formId: string) => void;
  getActiveForm: () => FormContent | undefined;
  updateActiveForm: (patch: Partial<FormContent>) => void;
  /**
   * SP6 Task 8 — single mutator for the active form's `remittanceRole`.
   * Centralizes the role -> dependent-key invariant (`instrument` is only
   * valid for role `'payment'`; `appliedAmountField` only for `'coupon'`) so
   * every caller that ever changes a form's role — not just
   * FormRemittancePanel — clears the now-invalid dependent key(s) in the
   * SAME write. Mirrors `updateRemittance` / `setIdentity`: a dedicated
   * action owning a domain invariant, rather than callers hand-rolling the
   * clearing logic through the generic `updateActiveForm(patch)`.
   */
  setFormRemittanceRole: (role: RemittanceRole | undefined) => void;
  /**
   * Id-scoped form patch — mirrors `updateActiveForm` but resolves the target
   * by an explicit `formId` instead of the store's `activeFormId`. The draft
   * store is a singleton that persists `selectForm` across surfaces, so a
   * panel's `activeFormId` prop and the store's `activeFormId` can diverge.
   * Writes that must land on a specific form (the one a surface resolved for
   * its read) use this so read and write can never drift onto different forms.
   */
  updateForm: (formId: string, patch: Partial<FormContent>) => void;
  updateFormFields: (formId: string, fields: FieldContent[]) => void;
  /**
   * Id-scoped upsert of a per-destination mapping on a specific form.
   * Mirrors `updateForm` — resolves by explicit `formId`, not `activeFormId`,
   * so the caller's read and write can never drift onto different forms.
   * If a mapping for `destinationId` already exists it is REPLACED (no duplicates).
   */
  updateFormDestinationMapping: (
    formId: string,
    destinationId: string,
    mappings: FormDestinationMapping['mappings'],
  ) => void;
  /**
   * Pure selector — returns the mappings array for a given (formId, destinationId).
   * Returns [] when the form or destination entry is absent.
   */
  getFormDestinationMappings: (
    formId: string,
    destinationId: string,
  ) => FormDestinationMapping['mappings'];

  addField: (f: FieldContent) => void;
  updateField: (name: string, patch: Partial<FieldContent>) => void;
  deleteField: (name: string) => void;
  duplicateField: (name: string) => void;
  addBatchField: (name: string, dataType: FieldContent['dataType']) => void;
  deleteBatchField: (name: string) => void;

  setIdentity: (identity: FormIdentity | null) => void;

  /**
   * SP6 — job-level remittance config mutator. The single writer every
   * Remittance job-config section (Basics now; Allocation/BalancePolicy/
   * TransactionRules/TransactionFields in later SP6 tasks) goes through.
   * Seeds `groupingMode: 'coupon_cycle'` the first time remittance is
   * created; an existing groupingMode is never clobbered by an unrelated
   * patch.
   */
  updateRemittance: (patch: Partial<RemittanceJobConfig>) => void;
  /** P8 — replace the job-level constants map (job config, autosaved). Passing
   *  an empty map clears the node entirely (omitted from content). */
  setConstants: (constants: Record<string, ConstantDecl>) => void;

  setFormRecognition: (next: FormRecognitionConfig) => void;

  addProfile: () => void;
  updateProfile: (id: string, patch: Partial<Profile>) => void;
  removeProfile: (id: string) => void;

  addBehavior: (fieldName: string, stage: Stage, behavior: Behavior) => void;
  updateBehavior: (fieldName: string, stage: Stage, idx: number, behavior: Behavior) => void;
  deleteBehavior: (fieldName: string, stage: Stage, idx: number) => void;
  reorderBehaviors: (fieldName: string, stage: Stage, fromIdx: number, toIdx: number) => void;

  /**
   * P3c — field-editor → Automations tab hand-off. When the field drawer's
   * "Add a custom check" jumps to the Automations tab, it stashes the target
   * form + field + effect here; `AutomationsTab` consumes this on mount to open
   * the create drawer pre-seeded to that form and field, then clears it. The
   * `formId` is essential for multi-form job types — without it the create
   * drawer would default to the first form and persist the check onto the wrong
   * form (T10). `null` when no
   * hand-off is pending. Replaces the legacy `pendingEditRuleId` path (whose
   * only consumer was the now-retired RulesTab).
   */
  // E-035 — `returnTo` (a full path+search, e.g. `/admin/forms/jt-1?tab=fields`)
  // is set only for the field-editor bridge launch; the Automations tab uses
  // it to bounce the operator back to the originating fields screen on
  // Cancel/Finish instead of stranding them on the Automations tab.
  pendingAutomationDraft:
    | { formId: string; fieldName: string; effectKey: string; returnTo?: string }
    | null;
  setPendingAutomationDraft: (
    next: { formId: string; fieldName: string; effectKey: string; returnTo?: string } | null,
  ) => void;

  toggleRuleActive: (ruleId: string) => void;
  updateRuleActions: (ruleId: string, actions: ActionConfig[]) => void;

  /** Id-scoped rule CRUD — mirror `updateFormFields`: resolve the target form by
   *  explicit `formId` (not `activeFormId`) so the Automations surface, which lets
   *  the operator pick any form, never drifts read/write onto different forms. */
  addRuleToForm: (formId: string, input: CreateRuleAttachmentInput) => string;
  updateRuleInForm: (formId: string, ruleId: string, updates: Partial<RuleAttachment>) => void;
  deleteRuleFromForm: (formId: string, ruleId: string) => void;
  /** Atomic automation group write (Automations builder panel/stepper): removes
   *  `removeRuleIds` from the form and appends the freshly compiled `inputs` in
   *  ONE state update (one autosave) — never a remove-then-add pair that would
   *  let useDebouncedSave fire mid-swap with the group half-removed. `preserveIds[i]`,
   *  when set, overrides the fresh id `createRuleAttachment` would otherwise mint
   *  for `inputs[i]` (branch-edit round-trip keeps stable rule ids). Returns the
   *  final ids in input order, or `[]` when the form doesn't exist (the caller's
   *  failure signal — the draft is preserved and a save error is surfaced). */
  replaceAutomationRules: (
    formId: string,
    removeRuleIds: string[],
    inputs: CreateRuleAttachmentInput[],
    preserveIds?: (string | undefined)[],
  ) => string[];

  addExportDestination: (dest: ExportDestinationContent) => void;
  updateExportDestination: (id: string, patch: Partial<ExportDestinationContent>) => void;
  deleteExportDestination: (id: string) => void;
  updateExportMappings: (destId: string, mappings: ExportDestinationContent['mappings']) => void;
  updateExportStructure: (destId: string, structure: ExportStructureNode) => void;
  updateExportRouting: (destId: string, routingCondition: ConditionGroup | undefined) => void;

  reset: () => void;
}

export const useFormConfigStore = create<FormConfigState>((set, get) => ({
  version: null,
  contentV3: null,
  content: null,
  activeFormId: null,
  selectedFieldName: null,
  fieldEditorOpen: false,
  dirty: false,
  saving: false,
  lastSavedAt: null,
  conflicted: false,
  advancedMode: false,
  pendingAutomationDraft: null,

  setPendingAutomationDraft: (next) => set({ pendingAutomationDraft: next }),

  loadVersion: (v) => {
    const v3 = migrateContentToV3(v.content);
    set({
      version: v,
      ...withV3(v3),
      activeFormId: getDefaultForm(v3).id,
      dirty: false,
      saving: false,
      lastSavedAt: null,
    });
  },
  // Adopt a freshly-forked draft's identity while KEEPING the in-progress
  // content/dirty. Used when an edit on a published version triggers a fork:
  // loadVersion() would reset content to the published snapshot and clear
  // dirty, silently dropping the very edit that triggered the fork. Here the
  // edit stays in `contentV3`/`content` and `dirty` remains true, so the
  // debounced save (which only fires once state==='draft') persists it into
  // the new draft. Does NOT touch contentV3 — the triggering edit already lives there.
  adoptDraft: (v) => set({ version: v, saving: false }),
  absorbServerForm: (v, formId) => set((s) => {
    const serverV3 = migrateContentToV3(v.content);
    const slice = getFormContent(serverV3, formId);
    const localV3 = s.contentV3 ?? serverV3;
    const next = slice ? upsertFormContent(localV3, slice) : localV3;
    // dirty is deliberately preserved: the absorbed slice matches the server,
    // and any pre-existing local edits still need their autosave.
    return { version: v, saving: false, ...withV3(next) };
  }),
  markSaving: () => set({ saving: true }),
  markSaved: (newRevision) =>
    set((s) => ({
      saving: false,
      dirty: false,
      lastSavedAt: new Date(),
      // F-032 — adopt the incremented revision so the next autosave's
      // baseRevision matches the server row this save just produced.
      version: s.version ? { ...s.version, contentRevision: newRevision } : s.version,
    })),
  markSaveFailed: () => set({ saving: false }),
  markConflicted: () => set({ saving: false, conflicted: true }),
  // R-117: setSelectedField highlights a row but does NOT open the drawer.
  // Passing null also closes the drawer (used by onClose in FormConfigFieldDrawer).
  setSelectedField: (name) => set({ selectedFieldName: name, fieldEditorOpen: false }),
  // R-117: openFieldEditor selects the field AND opens the drawer (called by Edit menu item).
  openFieldEditor: (name) => set({ selectedFieldName: name, fieldEditorOpen: true }),
  setAdvancedMode: (on) => set({ advancedMode: on }),

  // --- Form-scoped selectors/actions ---------------------------------------
  // Switching forms clears `selectedFieldName` when the newly-active form has no
  // field of that name. Selection is name-keyed (F-010); a stale selection that
  // doesn't exist on the new form would make the name-keyed field drawer's
  // `find(f => f.name === selected)` return null and unmount mid-edit once
  // form-switching is wired into the UI (Plan 2 Tasks 3-9).
  selectForm: (formId) => set((s) => {
    const form = s.contentV3 ? getFormContent(s.contentV3, formId) : undefined;
    const stillPresent =
      s.selectedFieldName != null && (form?.fields.some((f) => f.name === s.selectedFieldName) ?? false);
    return { activeFormId: formId, ...(stillPresent ? {} : { selectedFieldName: null }) };
  }),
  // getActiveForm reads contentV3 directly (the source of truth) rather than
  // resolveV3: it's a pure selector with no need for the legacy-content migrate
  // fallback that the mutating actions use for direct-`content` test seeding.
  getActiveForm: () => {
    const s = get();
    return s.contentV3 ? activeForm(s.contentV3, s.activeFormId) : undefined;
  },
  updateActiveForm: (patch) => set((s) => patchActiveForm(s, (form) => ({ ...form, ...patch })) ?? {}),
  setFormRemittanceRole: (role) => set((s) => patchActiveForm(s, (form) => ({
    ...form,
    remittanceRole: role,
    instrument: role === 'payment' ? form.instrument : undefined,
    appliedAmountField: role === 'coupon' ? form.appliedAmountField : undefined,
  })) ?? {}),
  updateForm: (formId, patch) => set((s) => {
    const v3 = resolveV3(s);
    if (!v3) return {};
    const target = getFormContent(v3, formId);
    if (!target) return {};
    return { ...withV3(upsertFormContent(v3, { ...target, ...patch })), dirty: true };
  }),
  updateFormFields: (formId, fields) => set((s) => {
    const v3 = resolveV3(s);
    if (!v3) return {};
    const form = getFormContent(v3, formId);
    if (!form) return {};
    return { ...withV3(upsertFormContent(v3, { ...form, fields })), dirty: true };
  }),
  updateFormDestinationMapping: (formId, destinationId, mappings) => set((s) => {
    const v3 = resolveV3(s);
    if (!v3) return {};
    const target = getFormContent(v3, formId);
    if (!target) return {};
    const existing = target.exportMappings ?? [];
    const idx = existing.findIndex((m) => m.destinationId === destinationId);
    const nextEntry: FormDestinationMapping = { destinationId, mappings };
    const nextExportMappings =
      idx >= 0
        ? existing.map((m, i) => (i === idx ? nextEntry : m))
        : [...existing, nextEntry];
    return { ...withV3(upsertFormContent(v3, { ...target, exportMappings: nextExportMappings })), dirty: true };
  }),
  getFormDestinationMappings: (formId, destinationId) => {
    const s = get();
    const v3 = s.contentV3;
    if (!v3) return [];
    const form = getFormContent(v3, formId);
    return form?.exportMappings.find((m) => m.destinationId === destinationId)?.mappings ?? [];
  },

  // --- Fields (active form) ------------------------------------------------
  addField: (f) => set((s) => {
    const next = patchActiveForm(s, (form) => ({ ...form, fields: [...form.fields, f] }));
    return next ? { ...next, selectedFieldName: f.name } : {};
  }),
  updateField: (name, patch) => set((s) => {
    // F-010 — selection is keyed by the mutable field `name`. When a patch
    // renames the currently-selected field, advance `selectedFieldName` to
    // the new name so the name-keyed lookup (drawer + every section) keeps
    // matching. Without this the drawer's `find(f => f.name === selected)`
    // returns null on the next render and the drawer unmounts mid-edit.
    const renamedTo =
      typeof patch.name === 'string' && patch.name !== name ? patch.name : null;
    const next = patchActiveForm(s, (form) => ({
      ...form,
      fields: form.fields.map((f) => (f.name === name ? { ...f, ...patch } : f)),
    }));
    if (!next) return {};
    return {
      ...next,
      ...(renamedTo && s.selectedFieldName === name ? { selectedFieldName: renamedTo } : {}),
    };
  }),
  deleteField: (name) => set((s) => {
    const next = patchActiveForm(s, (form) => ({
      ...form,
      fields: form.fields.filter((f) => f.name !== name),
    }));
    if (!next) return {};
    return { ...next, selectedFieldName: s.selectedFieldName === name ? null : s.selectedFieldName };
  }),
  duplicateField: (name) => set((s) => {
    const v3 = resolveV3(s);
    if (!v3) return {};
    const form = activeForm(v3, s.activeFormId);
    const src = form.fields.find((f) => f.name === name);
    if (!src) return {};
    const copy = {
      ...src,
      fieldDefinitionId: crypto.randomUUID(),
      name: `${src.name}_copy`,
      displayOrder: form.fields.length,
    };
    return {
      ...withV3(upsertFormContent(v3, { ...form, fields: [...form.fields, copy] })),
      dirty: true,
      selectedFieldName: copy.name,
    };
  }),
  addBatchField: (name, dataType) => set((s) => patchActiveForm(s, (form) => {
    const field: FieldContent = {
      fieldDefinitionId: crypto.randomUUID(),
      name,
      label: name,
      dataType,
      scope: 'batch',
      stageMap: {
        'pre-export': [{ kind: 'compute', formula: { source: '' }, dependsOn: [] }],
        'export': [{ kind: 'emit', destinations: 'all' }],
      },
      displayOrder: form.fields.filter((f) => f.scope === 'batch').length,
    };
    return { ...form, fields: [...form.fields, field] };
  }) ?? {}),
  deleteBatchField: (name) => set((s) => patchActiveForm(s, (form) => ({
    ...form,
    fields: form.fields.filter((f) => !(f.name === name && f.scope === 'batch')),
  })) ?? {}),

  // --- Identity (job-level) ------------------------------------------------
  setIdentity: (identity) => set((s) => patchV3(s, (v3) => {
    const next = { ...v3 };
    if (identity === null) {
      delete next.identity;
    } else {
      next.identity = identity;
    }
    return next;
  }) ?? {}),

  // --- Remittance (job-level) -----------------------------------------------
  updateRemittance: (patch) => set((s) => patchV3(s, (v3) => ({
    ...v3,
    remittance: { groupingMode: 'coupon_cycle', ...v3.remittance, ...patch },
  })) ?? {}),

  // --- Constants (job-level) ------------------------------------------------
  // P8 — the whole map is replaced at once (the SectionCard owns row CRUD
  // locally). An empty map omits the node so a no-constants job's content
  // stays byte-identical.
  setConstants: (constants) => set((s) => patchV3(s, (v3) => {
    const next = { ...v3 };
    if (Object.keys(constants).length === 0) delete next.constants;
    else next.constants = constants;
    return next;
  }) ?? {}),

  // --- Recognition (active form) -------------------------------------------
  // Recognition/fingerprinting config lives on the active form in V3 (mirrored
  // into the legacy view's `content.formRecognition` via the default form) and
  // persists through the same single-writer draft autosave as every other
  // section (avoids the F-012 dual-write class of bug). Sample-template FILE
  // uploads still go through the /api/forms/:versionId/samples endpoints (they
  // need multipart); the returned ref is mirrored back into content here.
  setFormRecognition: (next) => set((s) =>
    patchActiveForm(s, (form) => ({ ...form, recognition: next })) ?? {},
  ),

  // --- Profiles (job-level) ------------------------------------------------
  addProfile: () => set((s) => patchV3(s, (v3) => {
    const profiles = v3.profiles ?? [];
    const id = `p_${crypto.randomUUID()}`;
    const hasFallback = profiles.some((p) => p.isFallback);
    const p = hasFallback
      ? createDefaultProfile(id, `Profile ${profiles.length}`)
      : createFallbackProfile(id, 'Default');
    return { ...v3, profiles: [...profiles, p] };
  }) ?? {}),
  updateProfile: (id, patch) => set((s) => patchV3(s, (v3) => ({
    ...v3,
    profiles: (v3.profiles ?? []).map((p) => (p.id === id ? { ...p, ...patch } : p)),
  })) ?? {}),
  removeProfile: (id) => set((s) => patchV3(s, (v3) => ({
    ...v3,
    profiles: (v3.profiles ?? []).filter((p) => p.id !== id || p.isFallback),
  })) ?? {}),

  // --- Behaviors (active form's fields) ------------------------------------
  addBehavior: (fieldName, stage, behavior) => set((s) =>
    patchActiveForm(s, (form) => mapFieldStage(form, fieldName, stage, (bs) => [...bs, behavior])) ?? {},
  ),
  updateBehavior: (fieldName, stage, idx, behavior) => set((s) =>
    patchActiveForm(s, (form) => mapFieldStage(form, fieldName, stage, (bs) => bs.map((b, i) => (i === idx ? behavior : b)))) ?? {},
  ),
  deleteBehavior: (fieldName, stage, idx) => set((s) =>
    patchActiveForm(s, (form) => mapFieldStage(form, fieldName, stage, (bs) => bs.filter((_, i) => i !== idx))) ?? {},
  ),
  reorderBehaviors: (fieldName, stage, fromIdx, toIdx) => set((s) =>
    patchActiveForm(s, (form) => mapFieldStage(form, fieldName, stage, (bs) => {
      const arr = [...bs];
      const [moved] = arr.splice(fromIdx, 1);
      if (moved) arr.splice(toIdx, 0, moved);
      return arr;
    })) ?? {},
  ),

  // --- Rules (active form) -------------------------------------------------
  // P3c (2026-07-08) — the active-form rule CRUD (addRule / updateRule /
  // deleteRule / reorderRules) was removed with the Rules tab. The Automations
  // tab authors rules through the id-scoped addRuleToForm / updateRuleInForm /
  // deleteRuleFromForm below. toggleRuleActive / updateRuleActions remain.
  toggleRuleActive: (ruleId) => set((s) => patchActiveForm(s, (form) => ({
    ...form,
    rules: form.rules.map((r) => (r.id === ruleId ? { ...r, isActive: !r.isActive } : r)),
  })) ?? {}),
  updateRuleActions: (ruleId, actions) => set((s) => patchActiveForm(s, (form) => ({
    ...form,
    rules: form.rules.map((r) => (r.id === ruleId ? { ...r, actions } : r)),
  })) ?? {}),

  addRuleToForm: (formId, input) => {
    let newId = '';
    set((s) => {
      const v3 = resolveV3(s);
      if (!v3) return {};
      const form = getFormContent(v3, formId);
      if (!form) return {};
      const groupCount = form.rules.filter(
        (r) => r.stage === input.stage && r.trigger === input.trigger,
      ).length;
      const rule = createRuleAttachment({
        ...input,
        displayOrder: input.displayOrder ?? groupCount,
        actions: input.actions ?? [],
      });
      newId = rule.id;
      return { ...withV3(upsertFormContent(v3, { ...form, rules: [...form.rules, rule] })), dirty: true };
    });
    return newId;
  },
  updateRuleInForm: (formId, ruleId, updates) => set((s) => {
    const v3 = resolveV3(s);
    if (!v3) return {};
    const form = getFormContent(v3, formId);
    if (!form) return {};
    return {
      ...withV3(upsertFormContent(v3, {
        ...form,
        rules: form.rules.map((r) => (r.id === ruleId ? { ...r, ...updates } : r)),
      })),
      dirty: true,
    };
  }),
  deleteRuleFromForm: (formId, ruleId) => set((s) => {
    const v3 = resolveV3(s);
    if (!v3) return {};
    const form = getFormContent(v3, formId);
    if (!form) return {};
    return {
      ...withV3(upsertFormContent(v3, {
        ...form,
        rules: form.rules.filter((r) => r.id !== ruleId),
      })),
      dirty: true,
    };
  }),

  replaceAutomationRules: (formId, removeRuleIds, inputs, preserveIds) => {
    // Verify form exists before creating rules
    const state = useFormConfigStore.getState();
    const v3 = resolveV3(state);
    if (!v3) return [];
    const form = getFormContent(v3, formId);
    if (!form) return [];

    const created = inputs.map((input, i) => {
      const rule = createRuleAttachment(input);
      const keep = preserveIds?.[i];
      return keep ? { ...rule, id: keep } : rule;
    });
    set((s) => {
      const v3 = resolveV3(s);
      if (!v3) return {};
      const form = getFormContent(v3, formId);
      if (!form) return {};
      const remove = new Set(removeRuleIds);
      return {
        ...withV3(upsertFormContent(v3, {
          ...form,
          rules: [...form.rules.filter((r) => !remove.has(r.id)), ...created],
        })),
        dirty: true,
      };
    });
    return created.map((r) => r.id);
  },

  // --- Export --------------------------------------------------------------
  // In V3 the destination (minus mappings) lives top-level in
  // `contentV3.exportDestinations`; per-destination `mappings` live on a form's
  // `exportMappings`. The legacy view round-trips both back together.
  // addExportDestination spans both levels (job-level dest + active-form
  // mapping), so it resolves manually rather than via a single-level reducer.
  addExportDestination: (dest) => set((s) => {
    const v3 = resolveV3(s);
    if (!v3) return {};
    const { mappings, ...destNoMappings } = dest;
    const form = activeForm(v3, s.activeFormId);
    const nextForm: FormContent = {
      ...form,
      exportMappings: [
        ...form.exportMappings.filter((m) => m.destinationId !== dest.id),
        { destinationId: dest.id, mappings: mappings ?? [] },
      ],
    };
    const next = upsertFormContent(
      { ...v3, exportDestinations: [...v3.exportDestinations, destNoMappings] },
      nextForm,
    );
    return { ...withV3(next), dirty: true };
  }),
  updateExportDestination: (id, patch) => set((s) => patchV3(s, (v3) => {
    // Destination-level fields only; mappings are owned per-form, so strip any
    // `mappings` key from the patch to preserve the JobDestinationContent shape.
    const { mappings: _ignored, ...destPatch } = patch;
    return {
      ...v3,
      exportDestinations: v3.exportDestinations.map((d) => {
        if (d.id !== id) return d;
        const merged = { ...d, ...destPatch };
        // E-038 residual — an ephemeral-basePath ack (`_ephemeralPathAckedFor`)
        // is only valid while the destination stays on the adapter it was
        // acked under. The identity <select>'s onChange fires a bare
        // `{ adapter }` patch that never touches `config`, so without this
        // the stale ack survives an adapter round-trip (filesystem -> other
        // -> filesystem, same basePath) and the F-012 soft durability
        // warning never re-fires. This is the single choke point for every
        // adapter-change path in the app (DestinationDetail's onChange and
        // any future caller), so clearing here — rather than only in that
        // one component — covers all of them.
        if ('adapter' in destPatch && destPatch.adapter !== d.adapter) {
          const { _ephemeralPathAckedFor: _dropped, ...restConfig } = merged.config ?? {};
          merged.config = restConfig;
        }
        return merged;
      }),
    };
  }) ?? {}),
  deleteExportDestination: (id) => set((s) => patchV3(s, (v3) => ({
    ...v3,
    exportDestinations: v3.exportDestinations.filter((d) => d.id !== id),
    // Drop this destination's mappings from every form.
    forms: v3.forms.map((f) => ({
      ...f,
      exportMappings: f.exportMappings.filter((m) => m.destinationId !== id),
    })),
  })) ?? {}),
  // TODO(plan3): mappings home to the ACTIVE form, but flattenToLegacyContent
  // merges per-destination mappings default-form-first / first-wins. So once
  // form-switching lands (Tasks 3-9), editing a destination's mappings while a
  // NON-default form is active will be SHADOWED by the default form's mappings
  // in the legacy `content` view (and thus in the autosave that still sends
  // `content`). The Export-UI migration must either pin mapping edits to the
  // default form or teach flatten/save to honor the active form. Harmless today:
  // Task 1 ships a single default form, so active form == default form.
  updateExportMappings: (destId, mappings) => set((s) => patchActiveForm(s, (form) => ({
    ...form,
    exportMappings: [
      ...form.exportMappings.filter((m) => m.destinationId !== destId),
      { destinationId: destId, mappings },
    ],
  })) ?? {}),
  updateExportStructure: (destId, structure) => set((s) => patchV3(s, (v3) => ({
    ...v3,
    exportDestinations: v3.exportDestinations.map((d) => (d.id === destId ? { ...d, structure } : d)),
  })) ?? {}),
  updateExportRouting: (destId, routingCondition) => set((s) => patchV3(s, (v3) => ({
    ...v3,
    exportDestinations: v3.exportDestinations.map((d) => (d.id === destId ? { ...d, routingCondition } : d)),
  })) ?? {}),

  reset: () => set({
    version: null, contentV3: null, content: null, activeFormId: null,
    selectedFieldName: null,
    dirty: false, saving: false, lastSavedAt: null, conflicted: false, advancedMode: false,
    pendingAutomationDraft: null,
  }),
}));
